import useDisabledButtonWithDebounce from '@/features/v2/hooks/useDissabledButtonWithDebounce';
import useScrollToError from '@/features/v2/hooks/useScrollToError';

import { useFormDataQuery } from '@/api/getters';
import { useFormMutation, useSubmitStagesMutation } from '@/api/setters';
import Button, {
  ButtonLoading,
  ButtonRightArrow,
} from '@/components/ui/Button';
import { formMutationDto } from '@/dto/formMutationDto';
import { useStepper } from '@/features/v2/lib/stepper';
import { scrollToTop } from '@/helpers/scrollToTop';

import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export type SubmitButton = {
  label?: string;
  hidden?: boolean;
  isDisabledProp?: boolean;
  nextStageOnSubmit?: boolean;
  isLoading?: boolean;
  uniqTestId?: string;
};
export const SubmitStageButton = ({
  hidden,
  label,
  isDisabledProp,
  nextStageOnSubmit = true,
  isLoading,
  uniqTestId,
}: SubmitButton) => {
  const { i18n } = useLingui();

  const methods = useFormContext();
  const formMutation = useFormMutation();

  const {
    step,
    innerStep,
    innerObject,
    currentStep,
    forceDisableButton,
    nextStep,
  } = useStepper();

  const handleNextStep = () => {
    nextStep();
    methods.reset();
    scrollToTop();
  };

  const submitStages = useSubmitStagesMutation({
    afterGetInvalidate: handleNextStep,
  });

  const { data, isFetching } = useFormDataQuery();

  const completeValidation = !!currentStep?.completeValidation?.(innerStep);

  const isFormValid = methods.formState.isValid;
  const isPending =
    formMutation.isPending || submitStages.isPending || isFetching;

  const { isDisabled } = useDisabledButtonWithDebounce();

  const buttonDisabled = completeValidation
    ? false || isDisabledProp
    : isDisabled || forceDisableButton || isDisabledProp;

  const { startSubmitting } = useScrollToError();

  const handleSubmit = async () => {
    if (data && nextStageOnSubmit) {
      await formMutation.mutateAsync({
        ...formMutationDto({
          form_data: methods.watch(),
          stepper: {
            step: step + 1,
            innerStep: { ...innerObject, [step + 1]: 0 },
          },
        }),
      });
      await submitStages.mutateAsync(data.stage);
    }
  };

  if (hidden) {
    return null;
  }

  return (
    <Button
      type={'submit'}
      onClick={isFormValid ? handleSubmit : startSubmitting}
      className={'w-full md:w-auto'}
      loading={formMutation.isPending || submitStages.isPending || isLoading}
      disabled={buttonDisabled || isPending || isLoading}
      data-testid={`submit-stage-button-${uniqTestId}`}
    >
      {label || i18n.t('Zakończ formularz')}
      <ButtonLoading />
      <ButtonRightArrow />
    </Button>
  );
};
